import React, { useState, useRef, useCallback, useEffect } from 'react';
import { baseColors } from '../../../Themes/colors';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { CircularProgress, ClickAwayListener, Menu, MenuItem, Paper, Popper, } from '@mui/material';
import _ from '../../../utils/lodash';
import { GlobalSearchWrapper, SearchResultsDropdown } from './GlobalSearchStyles';
var DEFAULT_MODULE_OPTIONS = [
    { label: 'Global', value: 'global' },
    { label: 'Module', value: 'module' },
];
var GlobalSearch = function (_a) {
    var _b;
    var _c = _a.moduleOptions, moduleOptions = _c === void 0 ? DEFAULT_MODULE_OPTIONS : _c, _d = _a.globalModuleValue, globalModuleValue = _d === void 0 ? 'global' : _d, _e = _a.globalSearchText, globalSearchText = _e === void 0 ? '' : _e, onGlobalSearch = _a.onGlobalSearch, _f = _a.searchText, searchText = _f === void 0 ? '' : _f, onSearchComplete = _a.onSearchComplete, _g = _a.searchResults, searchResults = _g === void 0 ? [] : _g, _h = _a.isSearching, isSearching = _h === void 0 ? false : _h, _j = _a.isLoadingMore, isLoadingMore = _j === void 0 ? false : _j, _k = _a.hasMore, hasMore = _k === void 0 ? false : _k, onLoadMore = _a.onLoadMore, onResultClick = _a.onResultClick, _l = _a.placeholder, placeholder = _l === void 0 ? 'Search By Application ID, Name' : _l, _m = _a.debounceTimer, debounceTimer = _m === void 0 ? 300 : _m, _o = _a.minSearchLength, minSearchLength = _o === void 0 ? 3 : _o, _p = _a.hasError, hasError = _p === void 0 ? false : _p, _q = _a.errorMessage, errorMessage = _q === void 0 ? 'Something went wrong. Please try again.' : _q;
    var _r = useState(moduleOptions[0]), selectedModule = _r[0], setSelectedModule = _r[1];
    var _s = useState(''), inputValue = _s[0], setInputValue = _s[1];
    var _t = useState(false), isFocused = _t[0], setIsFocused = _t[1];
    var _u = useState(null), menuAnchor = _u[0], setMenuAnchor = _u[1];
    var _v = useState(false), isDebouncePending = _v[0], setIsDebouncePending = _v[1];
    var wrapperRef = useRef(null);
    var inputRef = useRef(null);
    var isGlobalMode = selectedModule.value === globalModuleValue;
    useEffect(function () {
        var externalValue = isGlobalMode ? globalSearchText : searchText;
        setInputValue(externalValue || '');
    }, [isGlobalMode, globalSearchText, searchText]);
    var debouncedSearch = useCallback(_.debounce(function (value, globalMode) {
        setIsDebouncePending(false);
        if (globalMode) {
            onGlobalSearch === null || onGlobalSearch === void 0 ? void 0 : onGlobalSearch(value);
        }
        else {
            onSearchComplete === null || onSearchComplete === void 0 ? void 0 : onSearchComplete(value);
        }
    }, debounceTimer), [onGlobalSearch, onSearchComplete, debounceTimer]);
    useEffect(function () { return function () { return debouncedSearch.cancel(); }; }, [debouncedSearch]);
    var handleInputChange = function (e) {
        var val = e.target.value;
        setInputValue(val);
        setIsDebouncePending(val.trim().length >= minSearchLength);
        debouncedSearch(val, isGlobalMode);
    };
    var handleFocus = function () {
        setIsFocused(true);
    };
    var handleClickAway = function () {
        setIsFocused(false);
    };
    var handleModuleMenuOpen = function (e) {
        setMenuAnchor(e.currentTarget);
    };
    var handleModuleMenuClose = function () {
        setMenuAnchor(null);
    };
    var handleResultsScroll = function (e) {
        if (!hasMore || isSearching || isDebouncePending || isLoadingMore || !onLoadMore)
            return;
        var target = e.currentTarget;
        var threshold = 24;
        var reachedBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - threshold;
        if (reachedBottom) {
            onLoadMore();
        }
    };
    var handleModuleSelect = function (option) {
        var _a;
        debouncedSearch.cancel();
        setIsDebouncePending(false);
        setSelectedModule(option);
        setInputValue('');
        onGlobalSearch === null || onGlobalSearch === void 0 ? void 0 : onGlobalSearch('');
        onSearchComplete === null || onSearchComplete === void 0 ? void 0 : onSearchComplete('');
        handleModuleMenuClose();
        (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    };
    var showDropdown = isFocused && inputValue.trim().length >= minSearchLength;
    var hasResults = searchResults.some(function (g) { return g.items.length > 0; });
    var isLoading = isSearching || isDebouncePending;
    return (React.createElement(ClickAwayListener, { onClickAway: handleClickAway },
        React.createElement("div", { ref: wrapperRef, style: { position: 'relative', width: '100%' } },
            React.createElement(GlobalSearchWrapper, { className: isFocused ? 'focused' : '' },
                isFocused && (React.createElement("button", { className: "gs-module-btn", onClick: handleModuleMenuOpen, type: "button" },
                    selectedModule.label,
                    React.createElement(KeyboardArrowDownIcon, null))),
                React.createElement("span", { className: "gs-search-icon" },
                    React.createElement(SearchIcon, null)),
                React.createElement("input", { ref: inputRef, className: "gs-input", value: inputValue, onChange: handleInputChange, onFocus: handleFocus, placeholder: placeholder, autoComplete: "off" })),
            React.createElement(Menu, { anchorEl: menuAnchor, open: Boolean(menuAnchor), onClose: handleModuleMenuClose, slotProps: {
                    paper: { elevation: 3, sx: { minWidth: 150, mt: 0.5, borderRadius: 2 } },
                } }, moduleOptions.map(function (option) { return (React.createElement(MenuItem, { key: option.value, selected: selectedModule.value === option.value, onClick: function () { return handleModuleSelect(option); }, sx: {
                    fontSize: 16,
                    fontWeight: 400,
                    '&:hover': {
                        backgroundColor: baseColors.successPreset[20],
                    },
                    '&.Mui-selected': {
                        backgroundColor: baseColors.successPreset[20],
                        color: baseColors.successPreset[10],
                        '&:hover': {
                            backgroundColor: baseColors.successPreset[20],
                        },
                    },
                } }, option.label)); })),
            showDropdown && (React.createElement(Popper, { open: true, anchorEl: wrapperRef.current, placement: "bottom-start", style: {
                    width: (_b = wrapperRef.current) === null || _b === void 0 ? void 0 : _b.offsetWidth,
                    zIndex: 1300,
                }, modifiers: [{ name: 'offset', options: { offset: [0, 6] } }] },
                React.createElement(Paper, { elevation: 0 },
                    React.createElement(SearchResultsDropdown, { onScroll: handleResultsScroll },
                        isLoading && (React.createElement("div", { className: "result-searching" },
                            React.createElement(CircularProgress, { size: 14, thickness: 5 }),
                            "Searching\u2026")),
                        !isLoading && hasError && (React.createElement("div", { className: "result-error" }, errorMessage)),
                        !isLoading && !hasError && !hasResults && (React.createElement("div", { className: "result-empty" }, "No results found")),
                        !isLoading &&
                            !hasError &&
                            hasResults &&
                            searchResults.map(function (group) {
                                return group.items.length > 0 ? (React.createElement(React.Fragment, { key: group.label },
                                    React.createElement("div", { className: "result-group-header" }, group.label),
                                    group.items.map(function (item, idx) { return (React.createElement("div", { key: "".concat(group.label, "-").concat(idx), className: "result-item", onClick: function () {
                                            onResultClick === null || onResultClick === void 0 ? void 0 : onResultClick(item, group.label);
                                            setIsFocused(false);
                                            setInputValue('');
                                        } },
                                        React.createElement("span", { className: "result-name" }, item.name),
                                        React.createElement("span", { className: "result-id" }, item.id))); }))) : null;
                            }),
                        isLoadingMore && (React.createElement("div", { className: "result-load-more" },
                            React.createElement(CircularProgress, { size: 14, thickness: 5 }),
                            "Loading more\u2026")))))))));
};
export default GlobalSearch;
